<template>
  <div class="pk-root">
    <!-- 车牌展示行 -->
    <div class="pk-plate-row">
      <div
        v-for="(char, idx) in chars"
        :key="idx"
        :class="['pk-plate-cell', {
          'is-active': idx === activeIdx,
          'is-filled': !!char,
          'is-dot': idx === 2,
          'is-new': idx === 7,
          'is-empty': !char && idx !== 7
        }]"
        @mousedown.prevent
        @click="setActive(idx)"
      >
        <template v-if="idx === 2 && !chars[2]">
          <span class="pk-dot-sep">·</span>
        </template>
        <template v-else-if="idx === 7 && !char">
          <span class="pk-new-badge">新能源</span>
        </template>
        <template v-else>{{ char }}</template>

        <!-- 光标动画 -->
        <span v-if="idx === activeIdx && !char" class="pk-cursor"></span>
      </div>
    </div>

    <!-- 键盘主体 -->
    <div class="pk-keyboard">
      <!-- 第 0 位：省份 -->
      <template v-if="activeIdx === 0">
        <div class="pk-section-label">选择省份</div>
        <div class="pk-grid pk-grid-province">
          <button
            v-for="p in provinces"
            :key="p"
            type="button"
            class="pk-key pk-key-province"
            @mousedown.prevent
            @click="pressKey(p)"
          >{{ p }}</button>
        </div>
      </template>

      <!-- 第 1 位：字母 A–Z（无 I/O） -->
      <template v-else-if="activeIdx === 1">
        <div class="pk-section-label">选择字母</div>
        <div class="pk-grid pk-grid-alpha">
          <button
            v-for="c in letters"
            :key="c"
            type="button"
            class="pk-key"
            @mousedown.prevent
            @click="pressKey(c)"
          >{{ c }}</button>
        </div>
        <div class="pk-actions">
          <button type="button" class="pk-key pk-key-del" @mousedown.prevent @click="deleteLast">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"></path><line x1="18" y1="9" x2="12" y2="15"></line><line x1="12" y1="9" x2="18" y2="15"></line></svg>
            删除
          </button>
        </div>
      </template>

      <!-- 第 2–7 位：字母 + 数字 -->
      <template v-else>
        <div class="pk-section-label">
          第 {{ activeIdx + 1 }} 位
          <span v-if="activeIdx === 7" class="pk-new-tip">（新能源可选）</span>
        </div>
        <div class="pk-grid pk-grid-num">
          <button
            v-for="c in digits"
            :key="'d' + c"
            type="button"
            class="pk-key pk-key-num"
            @mousedown.prevent
            @click="pressKey(c)"
          >{{ c }}</button>
        </div>
        <div class="pk-grid pk-grid-alpha">
          <button
            v-for="c in letters"
            :key="'l' + c"
            type="button"
            class="pk-key"
            @mousedown.prevent
            @click="pressKey(c)"
          >{{ c }}</button>
        </div>
        <div class="pk-actions">
          <button type="button" class="pk-key pk-key-del" @mousedown.prevent @click="deleteLast">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"></path><line x1="18" y1="9" x2="12" y2="15"></line><line x1="12" y1="9" x2="18" y2="15"></line></svg>
            删除
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
const PROVINCES = ['京','津','沪','渝','冀','豫','云','辽','黑','湘','皖','鲁','新','苏','浙','赣','鄂','桂','甘','晋','蒙','陕','吉','闽','贵','粤','川','青','藏','琼','宁']
const LETTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ'.split('')
const DIGITS = '0123456789'.split('')

export default {
  name: 'PlateKeyboard',
  props: {
    value: { type: String, default: '' }
  },
  data() {
    const chars = Array(8).fill('')
    if (this.value) this.value.split('').forEach((c, i) => { if (i < 8) chars[i] = c })
    return {
      chars,
      activeIdx: this.value ? Math.min(this.value.length, 7) : 0,
      provinces: PROVINCES,
      letters: LETTERS,
      digits: DIGITS
    }
  },
  computed: {
    plateValue() {
      return this.chars.join('').trim()
    }
  },
  watch: {
    value(v) {
      if ((v || '') === this.plateValue) return
      const chars = Array(8).fill('')
      if (v) v.split('').forEach((c, i) => { if (i < 8) chars[i] = c })
      this.chars = chars
      // 外部直接输入（如文本框打字）时光标跟到末尾。最后修改：2026-10-10
      this.activeIdx = v ? Math.min(v.length, 7) : 0
    }
  },
  methods: {
    setActive(idx) {
      const lastFilled = this.chars.reduce((acc, c, i) => c ? i : acc, -1)
      if (idx <= lastFilled + 1) this.activeIdx = idx
    },
    pressKey(key) {
      if (this.activeIdx >= 8) return
      this.$set(this.chars, this.activeIdx, key)
      if (this.activeIdx < 7) this.activeIdx++
      this.emit()
    },
    deleteLast() {
      // 当前格有值先清当前，否则退一格
      if (this.chars[this.activeIdx]) {
        this.$set(this.chars, this.activeIdx, '')
      } else if (this.activeIdx > 0) {
        this.activeIdx--
        this.$set(this.chars, this.activeIdx, '')
      }
      this.emit()
    },
    emit() {
      this.$emit('input', this.plateValue)
      this.$emit('change', this.plateValue)
    }
  }
}
</script>

<style scoped>
.pk-root {
  font-family: -apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  user-select: none;
  -webkit-user-select: none;
}

/* ── 车牌展示行 ── */
.pk-plate-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 12px 8px 14px;
  background: #f8f9fc;
  border-radius: 10px 10px 0 0;
  border-bottom: 1px solid #e8eaef;
}

.pk-plate-cell {
  position: relative;
  width: 38px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #fff;
  border: 1.5px solid #d0d5e0;
  color: #1a1d23;
  box-shadow: 0 1px 3px rgba(0,0,0,.06);
}

.pk-plate-cell.is-active {
  border-color: var(--btn-primary, #2563eb);
  box-shadow: 0 0 0 3px rgba(64,158,255,.15), 0 1px 4px rgba(64,158,255,.2);
}

.pk-plate-cell.is-filled {
  background: #fff;
  color: #1a1d23;
}

.pk-plate-cell.is-new {
  width: 32px;
  border-style: dashed;
  border-color: #c8cdd8;
  background: #f8f9fc;
}

.pk-plate-cell.is-new.is-active {
  border-style: solid;
  border-color: var(--btn-primary, #2563eb);
  background: #fff;
}

.pk-dot-sep {
  font-size: 22px;
  color: #c0c4cc;
  font-weight: 400;
}

.pk-new-badge {
  font-size: 9px;
  font-weight: 500;
  color: #909399;
  line-height: 1.3;
  text-align: center;
  letter-spacing: 0;
}

/* 光标闪烁 */
.pk-cursor {
  position: absolute;
  bottom: 5px;
  left: 50%;
  transform: translateX(-50%);
  width: 16px;
  height: 2px;
  background: var(--btn-primary, #2563eb);
  border-radius: 2px;
  animation: blink 1s step-end infinite;
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* ── 键盘主体 ── */
.pk-keyboard {
  background: #eef0f5;
  border-radius: 0 0 10px 10px;
  padding: 10px 10px 12px;
}

.pk-section-label {
  font-size: 11px;
  color: #909399;
  font-weight: 500;
  letter-spacing: .5px;
  margin-bottom: 8px;
  padding-left: 2px;
  text-transform: uppercase;
}

.pk-new-tip {
  color: #67c23a;
  text-transform: none;
  font-weight: 400;
}

/* 网格布局 */
.pk-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 6px;
}

.pk-grid-province {
  gap: 5px;
}

.pk-grid-num {
  gap: 5px;
}

/* 通用按键 */
.pk-key {
  height: 36px;
  min-width: 34px;
  padding: 0 6px;
  border: none;
  border-radius: 6px;
  background: #fff;
  color: #1a1d23;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  max-width: 50px;
  transition: background 0.12s ease, transform 0.08s ease, box-shadow 0.12s ease;
  box-shadow: 0 1px 0 #c5c8d2, 0 1px 3px rgba(0,0,0,.1);
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.pk-key:hover {
  background: #ecf5ff;
  color: var(--btn-primary, #2563eb);
}

.pk-key:active {
  transform: translateY(1px);
  box-shadow: 0 0 0 #c5c8d2;
  background: var(--btn-primary, #2563eb);
  color: #fff;
}

/* 省份键稍小 */
.pk-key-province {
  font-size: 15px;
  height: 38px;
}

/* 数字键 */
.pk-key-num {
  font-size: 15px;
  max-width: 42px;
  letter-spacing: 0;
}

/* 操作行 */
.pk-actions {
  display: flex;
  gap: 6px;
  margin-top: 2px;
}

/* 删除键 */
.pk-key-del {
  flex: 0 0 auto;
  min-width: 72px;
  max-width: 72px;
  background: #fff0f0;
  color: #f56c6c;
  box-shadow: 0 1px 0 #e8b4b4, 0 1px 3px rgba(245,108,108,.15);
  font-size: 13px;
  font-weight: 500;
}

.pk-key-del:hover {
  background: #fde8e8;
  color: #e64545;
}

.pk-key-del:active {
  background: #f56c6c;
  color: #fff;
}

</style>
